// shared/ui-text.ts — every piece of wording an admin can change.
//
// A key is registered here with its default and a group. The app reads text
// through t(key) (see app/src/lib/text.tsx), and Back Office › Admin › Wording
// lists this registry so anything in it can be renamed without a deploy.
//
// Why a registry rather than "make every string editable": a key that nothing
// renders is a promise the UI does not keep, and a string with no stable key
// silently loses its override the next time the code around it changes. Adding a
// key here and using t() for it is one line, so the list grows as we go.
//
// Shared because the worker validates keys on save — an override for a key that
// does not exist is rejected rather than stored forever.

export interface TextEntry { key: string; default: string; group: string; hint?: string }

export const UI_TEXT: TextEntry[] = [
  // ── Store (the till) ────────────────────────────────────────────────
  { key: "store.tab.sell", default: "Sell", group: "Store", hint: "First tab in the till" },
  { key: "store.tab.workshop", default: "Workshop", group: "Store" },
  { key: "store.tab.receipts", default: "Receipts", group: "Store" },
  { key: "store.basket", default: "Basket", group: "Store", hint: "Some shops say Cart or Order" },
  { key: "store.basket.empty", default: "Basket is empty", group: "Store" },
  { key: "store.basket.empty.hint", default: "Scan or tap a product to add it here.", group: "Store" },
  { key: "store.checkout", default: "Checkout", group: "Store", hint: "On the floating basket button" },
  { key: "store.charge", default: "Charge", group: "Store", hint: "The button that takes the money" },
  { key: "store.search", default: "Search products", group: "Store" },
  { key: "store.scan", default: "Scan", group: "Store" },
  { key: "store.customer.walkin", default: "Walk-in", group: "Store", hint: "Shown when a sale has no customer" },

  // ── Status words shown as badges ────────────────────────────────────
  { key: "status.paid", default: "Paid", group: "Statuses" },
  { key: "status.pending", default: "Awaiting payment", group: "Statuses" },
  { key: "status.refunded", default: "Refunded", group: "Statuses" },
  { key: "status.void", default: "Voided", group: "Statuses" },
  { key: "status.estimate", default: "Estimate", group: "Statuses", hint: "Some shops say Quote" },
  { key: "status.in_progress", default: "In progress", group: "Statuses" },
  { key: "status.not_invoiced", default: "Done — not invoiced", group: "Statuses" },
  { key: "status.cancelled", default: "Cancelled", group: "Statuses" },
  { key: "stock.in", default: "in stock", group: "Statuses" },
  { key: "stock.low", default: "Low", group: "Statuses" },
  { key: "stock.out", default: "Out", group: "Statuses" },

  // ── Receipts tab ────────────────────────────────────────────────────
  { key: "receipts.kpi.taken", default: "Taken", group: "Receipts" },
  { key: "receipts.kpi.outstanding", default: "Outstanding", group: "Receipts" },
  { key: "receipts.kpi.billed", default: "Billed", group: "Receipts" },
  { key: "receipts.kpi.store", default: "Store", group: "Receipts" },
  { key: "receipts.kpi.workshop", default: "Workshop", group: "Receipts" },
  { key: "receipts.chart", default: "Takings per day", group: "Receipts" },
  { key: "receipts.view", default: "View / print", group: "Receipts" },
  { key: "receipts.invoice", default: "Invoice", group: "Receipts" },
  { key: "receipts.on_account", default: "On account", group: "Receipts" },

  // ── Workshop ────────────────────────────────────────────────────────
  { key: "workshop.job", default: "Job", group: "Workshop", hint: "Used across the workshop screens" },
  { key: "workshop.jobs", default: "Jobs", group: "Workshop" },
  { key: "workshop.newjob", default: "New job", group: "Workshop" },
  { key: "workshop.diary", default: "Diary", group: "Workshop", hint: "Some shops say Bookings or Calendar" },
  { key: "workshop.reminders", default: "Reminders", group: "Workshop" },
  { key: "workshop.bay", default: "Bay board", group: "Workshop" },
  { key: "workshop.technician", default: "Technician", group: "Workshop" },
  { key: "workshop.vehicle", default: "Vehicle", group: "Workshop", hint: "Bike, scooter, machine…" },

  // ── Diary ───────────────────────────────────────────────────────────
  { key: "diary.book", default: "Book in", group: "Diary" },
  { key: "diary.booking", default: "Booking", group: "Diary" },
  { key: "diary.today", default: "Today", group: "Diary" },
  { key: "diary.unassigned", default: "No technician yet", group: "Diary" },
  { key: "diary.arrived", default: "Arrived", group: "Diary" },
  { key: "diary.noshow", default: "No-show", group: "Diary" },

  // ── Reminders ───────────────────────────────────────────────────────
  { key: "reminders.mot", default: "MOT", group: "Reminders" },
  { key: "reminders.service", default: "Service", group: "Reminders" },
  { key: "reminders.overdue", default: "Overdue", group: "Reminders" },
  { key: "reminders.due", default: "Due soon", group: "Reminders" },
  { key: "reminders.call", default: "Call", group: "Reminders" },

  // ── Catalogue ───────────────────────────────────────────────────────
  { key: "catalogue.product", default: "Product", group: "Catalogue" },
  { key: "catalogue.cost", default: "Cost", group: "Catalogue" },
  { key: "catalogue.sell", default: "Sell", group: "Catalogue" },
  { key: "catalogue.available", default: "Available", group: "Catalogue" },
  { key: "catalogue.supplier", default: "Supplier", group: "Catalogue" },

  // ── Shared actions ──────────────────────────────────────────────────
  { key: "action.save", default: "Save", group: "Buttons" },
  { key: "action.cancel", default: "Cancel", group: "Buttons" },
  { key: "action.edit", default: "Edit", group: "Buttons" },
  { key: "action.remove", default: "Remove", group: "Buttons" },
  { key: "action.print", default: "Print", group: "Buttons" },
];

export const TEXT_KEYS = new Set(UI_TEXT.map((e) => e.key));
export const TEXT_DEFAULTS: Record<string, string> =
  Object.fromEntries(UI_TEXT.map((e) => [e.key, e.default]));
export const TEXT_GROUPS = Array.from(new Set(UI_TEXT.map((e) => e.group)));
